import {
	IModel,
	db,
	IEventable,
	IIdentifiable,
	IEventInfo,
	enumOrDefault,
	VIPUser,
	IVIPUser,
	IVIPUserOptions,
	VIPCapability,
	IVIPCapability,
	IVIPCapabilityOptions,
	VIPCapabilityRequestEvent,
	IVIPCapabilityRequestEvent,
	IVIPCapabilityRequestEventOptions,
} from "@/loader";
import dayjs, { Dayjs } from "dayjs";

export enum VipCapabilityRequestStatus {
	draft = "draft",
	sent = "sent",
	answered = "answered",
}

export function vipCapabilityRequestStatusOrDefault(value: any, defaultValue: VipCapabilityRequestStatus = VipCapabilityRequestStatus.draft): VipCapabilityRequestStatus {
	return <VipCapabilityRequestStatus>enumOrDefault(value, defaultValue, Object.values(VipCapabilityRequestStatus));
}

export function getVipCapabilityRequestStatusLabel(value: VipCapabilityRequestStatus): string {
	switch (value) {
		case VipCapabilityRequestStatus.draft:
			return window.wp.i18n._x("Draft", "Capability Request", "vip-vue");
		case VipCapabilityRequestStatus.sent:
			return window.wp.i18n._x("Sent", "Capability Request", "vip-vue");
		case VipCapabilityRequestStatus.answered:
			return window.wp.i18n._x("Answered", "Capability Request", "vip-vue");
		default:
			return window.wp.i18n._x("Unknown Status", "Capability Request", "vip-vue");
	}
}

export function getVipCapabilityRequestStatusList(): { value: any, text: string }[] {
	return [
		{ value: "draft", text: window.wp.i18n._x("Draft", "Capability Request", "vip-vue") },
		{ value: "sent", text: window.wp.i18n._x("Sent", "Capability Request", "vip-vue") },
		{ value: "answered", text: window.wp.i18n._x("Answered", "Capability Request", "vip-vue") },
	];
}

export type VIPCapabilityRequestFields = "id" | "fk_user" | "date" | "status";

/**
 * Capability Request Base Interface.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export interface IVIPCapabilityRequestBase extends IIdentifiable {
	/** Id */
	id?: number;
	/** User ID */
	fk_user?: number | null;
	/** Date */
	date?: Dayjs;
	/** Status */
	status?: VipCapabilityRequestStatus;
	/** Event information used to create an event on various actions. */
	event?: IEventInfo | null;

	/** User */
	user?: IVIPUser | null;

	/** Capabilities */
	capabilities?: IVIPCapability[];
	/** Capability request events */
	events?: IVIPCapabilityRequestEvent[];
}

/**
 * Capability Request Base Options Interface.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export interface IVIPCapabilityRequestOptions {
	/** Indicates whether the Id should be included or not. Default true. */
	id?: boolean;
	/** Indicates whether the User ID should be included or not. Default true. */
	fk_user?: boolean;
	/** Indicates whether the Date should be included or not. Default true. */
	date?: boolean;
	/** Indicates whether the Status should be included or not. Default true. */
	status?: boolean;
	/** Indicates whether the User should be included or not. Default false. */
	user?: boolean | IVIPUserOptions;
	/** Indicates whether the Capabilities should be included or not. Default false. */
	capabilities?: boolean | IVIPCapabilityOptions;
	/** Indicates whether the Capability request events should be included or not. Default false. */
	events?: boolean | IVIPCapabilityRequestEventOptions;
}

/**
 * Capability Request Base Class Logic.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export class VIPCapabilityRequestBase implements IVIPCapabilityRequestBase, IModel<IVIPCapabilityRequestBase>, IEventable {
	/** Id */
	public id!: number;
	/** User ID */
	public fk_user!: number | null;
	/** Date */
	public date!: Dayjs;
	/** Status */
	public status!: VipCapabilityRequestStatus;
	/** Event information used to create an event on various actions. */
	public event: IEventInfo | null = null;

	/**
	 * Get User
	 */
	public get user(): VIPUser | null {
		return this.fk_user && db.users.get(this.fk_user) || null;
	}

	/**
	 * Set User
	 */
	public set user(value: VIPUser | null) {
		this.fk_user = Number.nullable(value?.id);
	}

	/**
	 * Get Capabilities
	 */
	public get capabilities(): VIPCapability[] {
		return db.capabilities.list.filter(x => x.fk_capability_request === this.id);
	}

	/**
	 * Get Capability request events
	 */
	public get events(): VIPCapabilityRequestEvent[] {
		return db.capabilityRequestEvents.list.filter(x => x.fk_capability_request === this.id);
	}

	/**
	 * Check if this Status is Draft.
	 *
	 * @return bool
	 */
	public get isStatusDraft(): boolean {
		return this.status === VipCapabilityRequestStatus.draft;
	}

	/**
	 * Check if this Status is Sent.
	 *
	 * @return bool
	 */
	public get isStatusSent(): boolean {
		return this.status === VipCapabilityRequestStatus.sent;
	}

	/**
	 * Check if this Status is Answered.
	 *
	 * @return bool
	 */
	public get isStatusAnswered(): boolean {
		return this.status === VipCapabilityRequestStatus.answered;
	}

	/**
	 * Get the Status label
	 *
	 * @return string
	 */
	public getStatusLabel(): string {
		return getVipCapabilityRequestStatusLabel(this.status);
	}

	/**
	 * Constructor.
	 * @param data
	 */
	constructor (data: IVIPCapabilityRequestBase = {}) {
		this.load(data);
	}

	/**
	 * Set all properties with the data provided or a default value.
	 *
	 * All properties will be set, either with the value provided or with their default value.
	 *
	 * If you want to set only a subset of this instance's properties, use {@link VIPCapabilityRequestBase.update} instead.
	 *
	 * @param data
	 */
	public load(data: IVIPCapabilityRequestBase) {
		this.id = Number.orDefault(data.id);
		this.fk_user = Number.nullable(data.fk_user);
		this.date = dayjs.orDefault(data.date);
		this.status = vipCapabilityRequestStatusOrDefault(data.status, 'draft');
		
		if (data.user) {
			db.users.insertOrUpdate(data.user);
		}
		
		if (Array.isArray(data.capabilities) && data.capabilities.length) {
			db.capabilities.insertOrUpdateMany(data.capabilities);
		}
		if (Array.isArray(data.events) && data.events.length) {
			db.capabilityRequestEvents.insertOrUpdateMany(data.events);
		}
	}

	/**
	 * Set the properties with the data provided.
	 *
	 * Only the properties defined in data will be updated. Falsy values will be treated, only `undefined` is not.
	 *
	 * If you want to redefine all properties, use {@link VIPCapabilityRequestBase.load} instead.
	 *
	 * @param data
	 */
	public update(data: IVIPCapabilityRequestBase) {
		if (data.id !== undefined) {
			this.id = Number.orDefault(data.id);
		}
		if (data.fk_user !== undefined) {
			this.fk_user = Number.nullable(data.fk_user);
		}
		if (data.date !== undefined) {
			this.date = dayjs.orDefault(data.date);
		}
		if (data.status !== undefined) {
			this.status = vipCapabilityRequestStatusOrDefault(data.status, 'draft');
		}
		
		if (data.user) {
			db.users.insertOrUpdate(data.user);
		}
		
		if (Array.isArray(data.capabilities) && data.capabilities.length) {
			db.capabilities.insertOrUpdateMany(data.capabilities);
		}
		if (Array.isArray(data.events) && data.events.length) {
			db.capabilityRequestEvents.insertOrUpdateMany(data.events);
		}
	}
}
